import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCan, useWarnAboutChange } from '@refinedev/core';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { DailyEngagementConfig, DailyEngagementConfigResponse } from '@/types';
import {
  TAB_PREFIXES,
  defaultDailyEngagementConfig,
  diffConfigs,
  errorsUnder,
  restoreNulls,
  splitServerErrors,
  validateDailyEngagementConfig,
} from './dailyEngagementConfig';
import { DraftContext, type DraftContextValue, type SwitchState } from './draftContext';
import { OverviewTab } from './OverviewTab';
import { DailyBonusTab } from './DailyBonusTab';
import { SpinTab } from './SpinTab';
import { FreezesTab } from './FreezesTab';
import { LoyaltyTab } from './LoyaltyTab';
import { FastClockTab } from './FastClockTab';
import { fastClockEnvironment } from './fastClock';

const { Title, Text } = Typography;
const { confirm } = Modal;

type ApiError = {
  response?: { status?: number; data?: { message?: string | string[] } };
};

const apiStatus = (error: unknown): number | undefined => (error as ApiError)?.response?.status;

const apiMessage = (error: unknown): string | undefined => {
  const detail = (error as ApiError)?.response?.data?.message;
  return Array.isArray(detail) ? detail.join('; ') : detail;
};

const readConfigReadonly = (): boolean => {
  try {
    const modules = JSON.parse(localStorage.getItem('admin_readonly_modules') || '{}') as Record<
      string,
      boolean
    >;
    return modules.config === true;
  } catch {
    return false;
  }
};

const TABS = ['overview', 'daily', 'spin', 'freezes', 'loyalty', 'fast-clock'] as const;
type TabKey = (typeof TABS)[number];

/**
 * Daily Rewards: one editor over the single dailyEngagementConfig document
 * (Daily Reward, spin wheel, coin bonus, freezes, loyalty). Every tab edits
 * the same draft; Save sends the whole document.
 */
export const DailyRewardsPage: React.FC = () => {
  const { data: canEditData } = useCan({ resource: 'config', action: 'edit' });
  const canEdit = canEditData?.can === true;
  // Undefined while the permission check is still running: not a denial.
  const cannotEdit = canEditData?.can === false;
  const { setWarnWhen } = useWarnAboutChange();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // The fast clock exists only on staging-like servers; the tab is offered
  // once the server says which environment it is (never on production).
  const [fastClockOffered, setFastClockOffered] = useState(false);
  const requestedTab: TabKey = TABS.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : 'overview';
  const activeTab: TabKey =
    requestedTab === 'fast-clock' && !fastClockOffered ? 'overview' : requestedTab;
  const setTab = (key: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [served, setServed] = useState<DailyEngagementConfigResponse | null>(null);
  const [draft, setDraft] = useState<DailyEngagementConfig | null>(null);
  const [switchState, setSwitchState] = useState<SwitchState>({ status: 'loading' });
  // The server's problems with the last rejected save, and the document sent,
  // so the list can be marked stale once the admin edits again.
  const [rejection, setRejection] = useState<{ sent: string; errors: string[] } | null>(null);
  const [readOnlyHit, setReadOnlyHit] = useState(false);
  const configReadonly = readConfigReadonly() || readOnlyHit;

  const load = useCallback((res: DailyEngagementConfigResponse) => {
    // The server strips null keys from responses; put them back once, so the
    // served copy, the draft and the diff all hold the complete document.
    const doc = restoreNulls(res.dailyEngagementConfig);
    setServed({ ...res, dailyEngagementConfig: doc });
    setDraft(structuredClone(doc));
    setRejection(null);
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await adminApi.getDailyEngagementConfig();
      load(res.data);
    } catch (error) {
      setLoadError(true);
      message.error(apiMessage(error) ?? 'Failed to load the Daily Rewards config');
    } finally {
      setLoading(false);
    }
  }, [load]);

  const fetchSwitch = useCallback(async () => {
    setSwitchState({ status: 'loading' });
    try {
      const res = await adminApi.getFeatureFlags();
      const flags = (res.data ?? {}) as Record<string, boolean>;
      // A missing row reads as ON on the server.
      setSwitchState({ status: 'ok', on: flags.daily_engagement_enabled !== false });
    } catch {
      setSwitchState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchSwitch();
  }, [fetchConfig, fetchSwitch]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getDiagnostics()
      .then((res) => {
        const nodeEnv = (res.data as { env?: { NODE_ENV?: string } } | undefined)?.env?.NODE_ENV;
        if (!cancelled) setFastClockOffered(fastClockEnvironment(nodeEnv));
      })
      .catch(() => {
        // Unknown environment: leave the staging-only tab hidden.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const errors = useMemo(() => (draft ? validateDailyEngagementConfig(draft) : []), [draft]);
  const changes = useMemo(
    () => (served && draft ? diffConfigs(served.dailyEngagementConfig, draft) : []),
    [served, draft]
  );
  const dirty = changes.length > 0;
  const draftJson = useMemo(() => (draft ? JSON.stringify(draft) : ''), [draft]);
  const rejectionIsStale = rejection !== null && rejection.sent !== draftJson;

  useEffect(() => {
    setWarnWhen(dirty);
  }, [dirty, setWarnWhen]);
  useEffect(() => () => setWarnWhen(false), [setWarnWhen]);

  const editable = canEdit && !configReadonly && draft !== null && !saving;

  const update = useCallback((change: (d: DailyEngagementConfig) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      change(next);
      return next;
    });
  }, []);

  const context = useMemo<DraftContextValue | null>(
    () => (draft ? { draft, editable, errors, update } : null),
    [draft, editable, errors, update]
  );

  const save = () => {
    if (!draft || errors.length > 0) return;
    const sent = draft;
    const sentJson = JSON.stringify(sent);
    confirm({
      title: `Save ${changes.length} change${changes.length === 1 ? '' : 's'} to Daily Rewards?`,
      icon: <ExclamationCircleOutlined />,
      content:
        'The whole document is replaced. Every server applies it within seconds, from the next read, claim, spin or vault opening. Past claims, spins and opened vaults keep what they granted.',
      okText: 'Save',
      onOk: async () => {
        setSaving(true);
        setRejection(null);
        try {
          const res = await adminApi.updateDailyEngagementConfig(sent);
          load(res.data);
          message.success('Daily Rewards config saved');
        } catch (error) {
          const status = apiStatus(error);
          const detail = apiMessage(error);
          if ((status === 400 || status === 422) && detail) {
            setRejection({ sent: sentJson, errors: splitServerErrors(detail) });
            message.error('The server rejected the config');
          } else if (status === 403 && detail?.includes('read-only')) {
            setReadOnlyHit(true);
          } else if (status !== 403) {
            // A 403 for permissions already shows the access notification.
            message.error(detail ?? 'Failed to save the Daily Rewards config');
          }
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const resetEdits = () => {
    if (!served) return;
    confirm({
      title: 'Discard your unsaved changes?',
      icon: <ExclamationCircleOutlined />,
      content: 'The editor goes back to the document the server is serving.',
      onOk: () => {
        setDraft(structuredClone(served.dailyEngagementConfig));
        setRejection(null);
      },
    });
  };

  const loadDefaults = () => {
    confirm({
      title: 'Load the GDD defaults into the editor?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Every field is replaced with the GDD v1.03 default, including the Day 28 card (back to a random Legendary). Nothing changes on the server until you save.',
      onOk: () => setDraft(defaultDailyEngagementConfig()),
    });
  };

  const reload = () => {
    if (!dirty) {
      fetchConfig();
      fetchSwitch();
      return;
    }
    confirm({
      title: 'Reload and discard your unsaved changes?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        fetchConfig();
        fetchSwitch();
      },
    });
  };

  const tabLabel = (label: string, key: keyof typeof TAB_PREFIXES) => {
    const count = TAB_PREFIXES[key].reduce((n, p) => n + errorsUnder(errors, p).length, 0);
    return (
      <Space size={6}>
        {label}
        {count > 0 && <Badge count={count} size="small" />}
      </Space>
    );
  };

  if (loadError && !served) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Couldn't load the Daily Rewards config"
          description="Nothing can be changed until the served document has loaded."
          action={<Button onClick={fetchConfig}>Retry</Button>}
        />
      </Card>
    );
  }

  if (loading && !served) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  if (!served || !context) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Daily Rewards
              </Title>
              <Text type="secondary">
                28-day Daily Reward, spin wheel, rewarded-video coin bonus, streak freezes and
                loyalty vaults: one document, saved whole.
              </Text>
            </div>
            <Space wrap>
              {dirty ? (
                <Tag color="gold">
                  {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
                </Tag>
              ) : (
                <Tag color="green">Saved</Tag>
              )}
              {errors.length > 0 && (
                <Tag color="red">
                  {errors.length} problem{errors.length === 1 ? '' : 's'}
                </Tag>
              )}
              <Button icon={<ReloadOutlined />} onClick={reload} disabled={saving || loading}>
                Reload
              </Button>
              {canEdit && !configReadonly && (
                <>
                  <Button onClick={loadDefaults} disabled={!editable}>
                    Load GDD defaults
                  </Button>
                  <Button onClick={resetEdits} disabled={!dirty || saving}>
                    Reset unsaved edits
                  </Button>
                  <Button
                    type="primary"
                    onClick={save}
                    loading={saving}
                    disabled={!dirty || errors.length > 0 || !editable}
                  >
                    Save
                  </Button>
                </>
              )}
            </Space>
          </Space>

          {configReadonly && (
            <Alert
              type="warning"
              showIcon
              message="Configuration is in read-only mode on the server"
              description="Saving is switched off for the config module (it applies to every admin, super admins included). You can still view the settings."
            />
          )}
          {cannotEdit && !configReadonly && (
            <Alert
              type="info"
              showIcon
              message="View only: changing these settings needs the config:edit permission."
            />
          )}
          {rejection && (
            <Alert
              type={rejectionIsStale ? 'warning' : 'error'}
              showIcon
              closable
              onClose={() => setRejection(null)}
              message={
                rejectionIsStale
                  ? 'The server rejected your last save. You have edited since; save again to re-check.'
                  : 'The server rejected the config'
              }
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {rejection.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              }
            />
          )}
          {dirty && errors.length > 0 && (
            <Text type="danger">
              Save is blocked until the {errors.length} problem{errors.length === 1 ? '' : 's'}{' '}
              marked on the tabs {errors.length === 1 ? 'is' : 'are'} fixed (the server runs the
              same checks).
            </Text>
          )}
        </Space>
      </Card>

      <DraftContext.Provider value={context}>
        <Card size="small">
          <Tabs
            activeKey={activeTab}
            onChange={setTab}
            destroyInactiveTabPane
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: (
                  <OverviewTab served={served} switchState={switchState} changes={changes} />
                ),
              },
              {
                key: 'daily',
                label: tabLabel('Daily bonus', 'daily'),
                children: <DailyBonusTab />,
              },
              { key: 'spin', label: tabLabel('Spin wheel', 'spin'), children: <SpinTab /> },
              { key: 'freezes', label: tabLabel('Freezes', 'freezes'), children: <FreezesTab /> },
              { key: 'loyalty', label: tabLabel('Loyalty', 'loyalty'), children: <LoyaltyTab /> },
              ...(fastClockOffered
                ? [
                    {
                      key: 'fast-clock',
                      label: 'Fast clock (staging)',
                      children: <FastClockTab canEdit={canEdit && !configReadonly} />,
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </DraftContext.Provider>
    </Space>
  );
};

export default DailyRewardsPage;
